import React from 'react';
import { Icon, IconCrown, IconEyeOff, IconMeetingLocked, IconMicrophone, IconShareDesktop, IconUserGroup } from '../../base/icons';
import { PARTICIPANT_ROLE } from '../../base/participants/constants';
import { Tooltip } from '../../base/tooltip';
/**
 * One badge in the row's icon stack.
 *
 * Kept as a render helper rather than a component so this file has a single
 * component export (react/no-multi-comp).
 *
 * @param {string} key - React key.
 * @param {Function} src - The icon.
 * @param {string} tooltipKey - The i18n key of the tooltip.
 * @param {boolean} firstInList - Whether the row is first in its list.
 * @param {string} className - Extra class for the badge.
 * @returns {JSX.Element}
 */
function renderBadge(key, src, tooltipKey, firstInList, className = '') {
    return (React.createElement(Tooltip, { className: 'small', content: tooltipKey, key: key, position: firstInList ? ' bottom' : 'top' },
        React.createElement("div", { className: `moderator-icon-participants participantpane-item-icons ${className}`.trim() },
            React.createElement(Icon, { src: src }))));
}
/**
 * The role and state badges of a participant row: owner, moderator, panelist,
 * presenter, hidden-from-audience, backstage, and held-in-room. Shared by the
 * meeting list, the server-roster lists (backstage, main room) and the
 * breakout room lists, so every list spells a role the same way.
 *
 * @param {IProps} props - Component props.
 * @returns {JSX.Element | null}
 */
const RoleIndicators = ({ firstInList, isBackstage, isHeld, isHidden, isHost, role }) => {
    const badges = [];
    if (isHost) {
        badges.push(renderBadge('host', IconCrown, 'participantsPane.role.host', firstInList, 'participant-role-host'));
    }
    else if (role === PARTICIPANT_ROLE.MODERATOR) {
        badges.push(renderBadge('moderator', IconCrown, 'settings.moderator', firstInList));
    }
    if (role === PARTICIPANT_ROLE.PANELIST) {
        badges.push(renderBadge('panelist', IconMicrophone, 'participantsPane.panelist', firstInList));
    }
    else if (role === PARTICIPANT_ROLE.PRESENTER) {
        badges.push(renderBadge('presenter', IconShareDesktop, 'webinar.role.presenter', firstInList));
    }
    if (isHidden) {
        badges.push(renderBadge('hidden', IconEyeOff, 'webinar.hiddenBadge', firstInList));
    }
    if (isBackstage) {
        badges.push(renderBadge('backstage', IconUserGroup, 'webinar.inBackstage', firstInList));
    }
    if (isHeld) {
        badges.push(renderBadge('held', IconMeetingLocked, 'webinar.heldBadge', firstInList, 'participant-role-held'));
    }
    if (!badges.length) {
        return null;
    }
    return React.createElement(React.Fragment, null, badges);
};
export default RoleIndicators;
